/**
 * AramaEkrani.js — Global sesli arama arayüzü
 * ---------------------------------------------------------------------------
 * App.js'in KÖKÜNDE render edilir; bu yüzden hangi sayfa açık olursa olsun
 * çalışır. Önceki mimaride arayüz sohbet odasının içindeydi ve oda açık
 * değilken arama tamamen ölüyordu.
 *
 * Görünümler:
 *   gelen  → tam ekran gelen arama (Android / web; iOS'ta CallKit gösterir)
 *   giden  → "Aranıyor…" tam ekran
 *   aktif  → tam ekran konuşma ekranı; ⌄ ile küçültülüp hap hâline gelir
 *
 * Kullanıcı isteği: Cevaplandığında SOHBET EKRANI değil, ARAMA EKRANI görünür.
 * ---------------------------------------------------------------------------
 */

import React, { useEffect, useState, useCallback, useRef } from "react";
import { useLanguage } from "../i18n/LanguageContext";
import AvatarImg from "./AvatarImg";
import callManager from "../services/callManager";

function sureFormatla(ms) {
  const t = Math.max(0, Math.floor(ms / 1000));
  const sa = Math.floor(t / 3600);
  const dk = Math.floor((t % 3600) / 60);
  const sn = t % 60;
  if (sa > 0) return `${sa}:${String(dk).padStart(2, "0")}:${String(sn).padStart(2, "0")}`;
  return `${String(dk).padStart(2, "0")}:${String(sn).padStart(2, "0")}`;
}

export default function AramaEkrani({ uyeler = [], uyeRenk, odaAdi = "", benimUid = "" }) {
  const { t } = useLanguage();
  const [d, setD] = useState(() => callManager.durumuAl());
  const [gecen, setGecen] = useState(0);
  const pillRef = useRef(null);
  const pillSurukleme = useRef(null);
  const pillTiklamayiEngelle = useRef(false);
  const [pillKonum, setPillKonum] = useState(null);

  const gorunenViewport = useCallback(() => {
    const vv = typeof window !== "undefined" ? window.visualViewport : null;
    return {
      left: vv?.offsetLeft || 0,
      top: vv?.offsetTop || 0,
      width: vv?.width || window.innerWidth,
      height: vv?.height || window.innerHeight,
    };
  }, []);

  const pillKonumunuSinirla = useCallback((left, top, width, height) => {
    const viewport = gorunenViewport();
    const pay = 8;
    const minLeft = viewport.left + pay;
    const minTop = viewport.top + pay;
    const maxLeft = Math.max(minLeft, viewport.left + viewport.width - width - pay);
    const maxTop = Math.max(minTop, viewport.top + viewport.height - height - pay);
    return {
      left: Math.min(Math.max(minLeft, left), maxLeft),
      top: Math.min(Math.max(minTop, top), maxTop),
      transform: "none",
    };
  }, [gorunenViewport]);

  useEffect(() => callManager.aboneOl(setD), []);

  // iOS'ta layout viewport, ekranda görünen visual viewport'tan daha büyük
  // olabiliyor. Döndürme/viewport değişiminde sürüklenmiş hapı tekrar içeri al.
  useEffect(() => {
    const sinirla = () => {
      if (!pillRef.current) return;
      const rect = pillRef.current.getBoundingClientRect();
      setPillKonum((onceki) => {
        if (!onceki) return onceki;
        const yeni = pillKonumunuSinirla(rect.left, rect.top, rect.width, rect.height);
        if (Math.abs(yeni.left - onceki.left) < 0.5 && Math.abs(yeni.top - onceki.top) < 0.5) {
          return onceki;
        }
        return yeni;
      });
    };
    const vv = window.visualViewport;
    window.addEventListener("resize", sinirla);
    vv?.addEventListener("resize", sinirla);
    vv?.addEventListener("scroll", sinirla);
    return () => {
      window.removeEventListener("resize", sinirla);
      vv?.removeEventListener("resize", sinirla);
      vv?.removeEventListener("scroll", sinirla);
    };
  }, [pillKonumunuSinirla]);

  // Süre sayacı — tamamen yerel, sunucuya sorulmaz.
  useEffect(() => {
    if (d.durum !== "aktif" && d.durum !== "giden") return undefined;
    const f = () => setGecen(Date.now() - (d.baslangic || Date.now()));
    f();
    const id = setInterval(f, 1000);
    return () => clearInterval(id);
  }, [d.durum, d.baslangic]);

  const uyeBul = useCallback(
    (uid) => uyeler.find((x) => String(x.uid) === String(uid)) || null,
    [uyeler]
  );

  const konusmaSinifi = (uid) => {
    const s = d.rtc?.sesSeviyeleri?.[String(uid)] || 0;
    if (s > 0.45) return "callSpeaking callSpeakingStrong";
    if (s > 0.05) return "callSpeaking";
    return "";
  };

  const pillBaslat = (e) => {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    const rect = pillRef.current?.getBoundingClientRect();
    if (!rect) return;
    pillTiklamayiEngelle.current = false;
    pillSurukleme.current = {
      pointerId: e.pointerId,
      basX: e.clientX,
      basY: e.clientY,
      sol: rect.left,
      ust: rect.top,
      genislik: rect.width,
      yukseklik: rect.height,
    };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const pillSurukle = (e) => {
    const s = pillSurukleme.current;
    if (!s || s.pointerId !== e.pointerId) return;
    const dx = e.clientX - s.basX;
    const dy = e.clientY - s.basY;
    if (Math.hypot(dx, dy) > 6) pillTiklamayiEngelle.current = true;
    setPillKonum(pillKonumunuSinirla(
      s.sol + dx,
      s.ust + dy,
      s.genislik,
      s.yukseklik
    ));
  };

  const pillBitir = (e) => {
    const s = pillSurukleme.current;
    if (!s || s.pointerId !== e.pointerId) return;
    pillSurukleme.current = null;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  };

  const pillTikla = (e) => {
    if (pillTiklamayiEngelle.current) {
      pillTiklamayiEngelle.current = false;
      e.preventDefault();
      return;
    }
    callManager.ekraniAc();
  };

  if (d.durum === "idle") return null;

  const katilanlar = (d.arama?.participants || []).filter((p) => p.state === "joined");
  const enFazla = d.arama?.maxParticipants || 6;
  const bagliSayisi = d.arama?.joinedCount ?? katilanlar.length;

  /* ── GELEN ARAMA (tam ekran) ─────────────────────────────────────────── */
  /* "Devam eden arama" (sen aranmıyorsun, odada zaten bir arama var) tam ekran
     AÇILMAZ — sohbet içindeki ince afiş yeterlidir. Tam ekran yalnızca
     GERÇEKTEN arandığında gösterilir. */
  if (d.durum === "gelen" && d.gelen && !d.gelen.devamEden) {
    const u = uyeBul(d.gelen.fromUid);
    const ad = d.gelen.fromName || (u ? u.ad : "") || odaAdi || t("call.someone");
    const devam = !!d.gelen.devamEden;

    return (
      <div className="callOverlay callIncoming" role="dialog" aria-modal="true">
        <div className="callCard">
          <div className="callPulse" aria-hidden="true">
            <span className="callPulseRing" />
            <span className="callPulseRing callPulseRing2" />
            <div className="callAvatarBig">
              {u ? (
                <AvatarImg avatar={u.avatar || null} renk={uyeRenk ? uyeRenk(u.uid, uyeler) : null}
                  initials={u.ad?.[0] || "?"} size={96} radius="50%" uid={u.uid} />
              ) : <span className="callAvatarFallback">🎙️</span>}
            </div>
          </div>

          <div className="callWho">{ad}</div>
          <div className="callSub">
            {devam ? t("call.ongoing") : (d.gelen.isGroup ? t("call.incomingGroup") : t("call.incoming"))}
          </div>

          <div className="callActions">
            <button className="callBtn callBtnDecline"
              onClick={() => callManager.reddet()} aria-label={t("call.decline")}>✕</button>
            <button className="callBtn callBtnAccept"
              onClick={() => callManager.cevapla(d.gelen.callId).catch(() => {})}
              aria-label={devam ? t("call.join") : t("call.accept")}>📞</button>
          </div>
          <div className="callActionLabels">
            <span>{t("call.decline")}</span>
            <span>{devam ? t("call.join") : t("call.accept")}</span>
          </div>
        </div>
      </div>
    );
  }

  /* ── KÜÇÜLTÜLMÜŞ HAP ─────────────────────────────────────────────────── */
  if (d.kucuk) {
    return (
      <button
        ref={pillRef}
        className="callPill"
        style={pillKonum || undefined}
        onClick={pillTikla}
        onPointerDown={pillBaslat}
        onPointerMove={pillSurukle}
        onPointerUp={pillBitir}
        onPointerCancel={pillBitir}
      >
        <span className="callBarDot" aria-hidden="true" />
        <span className="callPillText">
          {d.durum === "giden"
            ? t("call.calling")
            : (d.durum === "baglaniyor" ? t("call.connecting") : sureFormatla(gecen))}
        </span>
      </button>
    );
  }

  /* ── AKTİF / GİDEN — TAM EKRAN ARAMA EKRANI ──────────────────────────── */
  const gorunenler = katilanlar.length
    ? katilanlar
    : [{ uid: d.arama?.startedByUid, state: "joined" }];

  return (
    <div className="callOverlay callExpanded" role="dialog" aria-modal="true">
      <div className="callExpandedTop">
        <button className="callCollapseBtn" onClick={() => callManager.ekraniKucult()}
          aria-label={t("call.minimize")}>⌄</button>
        <div className="callExpandedTitle">
          {odaAdi || t("call.voiceCall")}
          {d.durum === "aktif" && <span className="callExpandedCount">{bagliSayisi}/{enFazla}</span>}
        </div>
        <div className="callExpandedTimer">
          {d.durum === "giden"
            ? t("call.calling")
            : (d.durum === "baglaniyor" ? t("call.connecting") : sureFormatla(gecen))}
        </div>
      </div>

      <div className="callGrid">
        {gorunenler.map((p) => {
          const u = uyeBul(p.uid);
          const benMi = String(p.uid) === String(benimUid);
          const baglanti = d.rtc?.baglantiDurumlari?.[String(p.uid)];
          return (
            <div key={p.uid || "ben"} className="callGridItem">
              <div className={`callGridAvatar ${konusmaSinifi(p.uid)}`}>
                <AvatarImg avatar={u?.avatar || null}
                  renk={uyeRenk && u ? uyeRenk(u.uid, uyeler) : null}
                  initials={u?.ad?.[0] || "?"} size={74} radius="50%" uid={p.uid} />
                {benMi && d.susturuldu && <span className="callMutedBadge">🔇</span>}
              </div>
              <div className="callGridName">{benMi ? t("call.you") : (u?.ad || t("call.someone"))}</div>
              {!benMi && baglanti && baglanti !== "connected" && (
                <div className="callGridState">{t("call.connecting")}</div>
              )}
            </div>
          );
        })}
      </div>

      <div className="callExpandedActions">
        <button className={`callRoundBtn ${d.susturuldu ? "callRoundBtnOn" : ""}`}
          onClick={() => callManager.susturmaDegistir()}
          aria-label={d.susturuldu ? t("call.unmute") : t("call.mute")}>
          {d.susturuldu ? "🔇" : "🎤"}
        </button>
        <button className={`callRoundBtn ${d.hoparlor ? "callRoundBtnActive" : ""}`}
          onClick={() => callManager.hoparlorDegistir()}
          aria-label={d.hoparlor ? t("call.earpiece") : t("call.speaker")}>
          {d.hoparlor ? "🔊" : "📱"}
        </button>
        <button className="callRoundBtn callRoundBtnEnd"
          onClick={() => callManager.ayril()} aria-label={t("call.leave")}>✕</button>
      </div>
      <div className="callExpandedLabels">
        <span>{d.susturuldu ? t("call.unmute") : t("call.mute")}</span>
        <span>{d.hoparlor ? t("call.speaker") : t("call.earpiece")}</span>
        <span>{t("call.leave")}</span>
      </div>
    </div>
  );
}
